import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import StreamSummaryCard from './streams/StreamSummaryCard';
import StreamEditDrawer from './streams/StreamEditDrawer';
import StreamLibraryPicker from './StreamLibraryPicker';
import { extractFileName } from './streams/streamUtils';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List as MuiList,
    ListItemButton,
    ListItemText,
    Typography as MuiTypography,
    Button as MuiButton,
} from '@mui/material';

const STREAM_TYPES = {
    RTSP: 'rtsp',
    UPLOADED: 'uploaded',
    FLUX: 'flux',
    FILE: 'file',
};

const ensureArray = (value) => {
    if (!value) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

const THUMBNAIL_CANDIDATE_KEYS = [
    'thumbnail_url',
    'thumbnail',
    'poster_url',
    'poster',
    'snapshot_url',
    'snapshot',
    'screenshot_url',
    'screenshot',
    'preview_url',
    'preview',
];

const MEDIA_THUMBNAIL_KEYS = ['thumbnail_url', 'thumbnail', 'poster', 'preview'];

const failedThumbnailCache = new Set();

const isLikelyInvalidThumbnail = (url) => {
    if (!url) {
        return true;
    }
    const normalised = url.toLowerCase();
    if (normalised.includes('/storage/profile/')) {
        return true;
    }
    return false;
};

const randomKey = () => `stream-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const coerceString = (value) => {
    if (value === null || typeof value === 'undefined') {
        return '';
    }
    return String(value);
};

const syncHiddenField = (fieldId, payload) => {
    if (!fieldId) {
        return;
    }

    const field = document.getElementById(fieldId);
    if (!field) {
        console.warn('[DisplayWallStreamsEditor] Hidden field not found:', fieldId);
        return;
    }

    try {
        field.value = JSON.stringify(payload);
    } catch (error) {
        console.error('[DisplayWallStreamsEditor] Failed to serialise payload', error);
    }
};

const baseCellStyle = {
    minHeight: '260px',
    border: '1px dashed rgba(233, 30, 99, 0.35)',
    borderRadius: '10px',
    padding: '12px',
    backgroundColor: '#faf5f8',
    transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
    position: 'relative',
};

const activeCellStyle = {
    borderColor: '#e91e63',
    backgroundColor: '#fff',
    boxShadow: '0 6px 18px rgba(233, 30, 99, 0.15)',
};

const emptyCellButtonStyle = {
    width: '100%',
    height: '100%',
    minHeight: '220px',
    border: '1px dashed rgba(233, 30, 99, 0.35)',
    borderRadius: '8px',
    color: '#e91e63',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontWeight: 500,
    gap: '0.75rem',
    margin: '0',
};

const DisplayWallStreamsEditor = ({
    fieldId,
    formId,
    streams = [],
    mediaFiles = [],
    fluxes = [],
    maxCells = 1,
    wall = {},
    translations = {},
    library = null,
    featureFlags = {},
}) => {
    const t = useCallback((key, fallback) => translations[key] || fallback, [translations]);
    const tWith = useCallback((key, fallback, replacements = {}) => {
        const template = translations[key] || fallback;
        return Object.entries(replacements).reduce((acc, [needle, value]) => acc.replace(new RegExp(needle, 'g'), value), template);
    }, [translations]);

    const describeFlux = useCallback((flux, { includeDevice = true } = {}) => {
        if (!flux || typeof flux !== 'object') {
            return '';
        }

        const parts = [];
        const name = typeof flux.name === 'string' ? flux.name.trim() : '';
        if (name) {
            parts.push(name);
        }

        if (typeof flux.id !== 'undefined' && flux.id !== null && `${flux.id}`.trim() !== '') {
            parts.push(tWith('fluxIdSuffix', 'Flux ID #:id', { ':id': flux.id }));
        }

        if (includeDevice && flux.device_id) {
            parts.push(tWith('fluxDeviceSuffix', 'Device #:id', { ':id': flux.device_id }));
        }

        return parts.join(' - ');
    }, [tWith]);

    const cols = Math.max(parseInt(wall?.grid?.cols, 10) || 1, 1);
    const rows = Math.max(parseInt(wall?.grid?.rows, 10) || 1, 1);
    const computedCells = Math.max(cols * rows, 1);

    const mediaMap = useMemo(() => {
        const map = new Map();
        ensureArray(mediaFiles).forEach((file) => {
            if (file && typeof file.id !== 'undefined') {
                map.set(coerceString(file.id), file);
            }
        });
        return map;
    }, [mediaFiles]);

    const fluxMap = useMemo(() => {
        const map = new Map();
        ensureArray(fluxes).forEach((flux) => {
            if (flux && typeof flux.id !== 'undefined') {
                map.set(coerceString(flux.id), flux);
            }
        });
        return map;
    }, [fluxes]);

    const resolvedFeatureFlags = useMemo(() => ({
        allowUploadedMedia: true,
        filterFluxTransportOnly: false,
        advancedFluxPicker: false,
        fluxPickerMaxHeightRatio: 0.5,
        ...featureFlags,
    }), [featureFlags]);

    const isFluxEligible = useCallback((flux) => {
        if (!flux || typeof flux !== 'object') {
            return false;
        }
        if (!resolvedFeatureFlags.filterFluxTransportOnly) {
            return true;
        }
        const value = flux.uploaded_link;
        if (typeof value === 'boolean') {
            return value === false;
        }
        if (value === null || typeof value === 'undefined') {
            return true;
        }
        const normalised = String(value).trim().toLowerCase();
        return normalised === '' || normalised === '0' || normalised === 'false' || normalised === 'no';
    }, [resolvedFeatureFlags.filterFluxTransportOnly]);

    const determineDefaultType = useCallback(() => STREAM_TYPES.RTSP, []);

    const buildStreamForType = useCallback((type, previous = {}) => {
        const base = {
            key: previous.key || randomKey(),
            title: previous.title ? previous.title : '',
            sourceType: type,
            sourceIdentifier: previous.sourceIdentifier ? coerceString(previous.sourceIdentifier) : '',
            url: previous.url ? coerceString(previous.url) : '',
            meta: previous.meta && typeof previous.meta === 'object' ? previous.meta : {},
        };

        if (type === STREAM_TYPES.UPLOADED) {
            let file = mediaMap.get(base.sourceIdentifier);
            if (!file) {
                file = mediaMap.values().next().value;
            }
            if (file) {
                base.sourceIdentifier = coerceString(file.id);
                base.url = file.path || '';
                base.meta = {
                    file_id: file.id,
                    file_path: file.path,
                    file_name: file.name,
                    upload_name: file.upload_name,
                    type: file.type,
                };
            } else {
                base.sourceIdentifier = '';
                base.url = '';
                base.meta = {};
            }
        } else if (type === STREAM_TYPES.FLUX) {
            let flux = fluxMap.get(base.sourceIdentifier);
            if (!flux || (!isFluxEligible(flux) && !base.sourceIdentifier)) {
                for (const candidate of fluxMap.values()) {
                    if (isFluxEligible(candidate)) {
                        flux = candidate;
                        break;
                    }
                }
            }
            if (flux) {
                const transportUrl = flux.link || '';
                base.sourceIdentifier = coerceString(flux.id);
                base.title = flux.name;
                base.url = transportUrl || '';
                base.meta = {
                    flux_id: flux.id,
                    link: flux.link,
                    uploaded_link: flux.uploaded_link,
                    device_id: flux.device_id,
                    transport_url: transportUrl,
                };
            } else {
                base.sourceIdentifier = '';
                base.url = '';
                base.meta = {};
            }
        } else if (type === STREAM_TYPES.FILE) {
            base.sourceIdentifier = '';
            base.meta = previous.meta && typeof previous.meta === 'object' ? previous.meta : {};
        } else {
            base.sourceIdentifier = previous.sourceIdentifier ? coerceString(previous.sourceIdentifier) : '';
            base.meta = previous.meta && typeof previous.meta === 'object' ? previous.meta : {};
        }

        return base;
    }, [fluxMap, isFluxEligible, mediaMap]);

    const normalisedInitial = useMemo(() => {
        return ensureArray(streams)
            .slice()
            .sort((a, b) => {
                const posA = typeof a?.position === 'number' ? a.position : 0;
                const posB = typeof b?.position === 'number' ? b.position : 0;
                return posA - posB;
            })
            .map((stream) => ({
                position: typeof stream?.position === 'number' ? stream.position : undefined,
                ...buildStreamForType((stream?.source_type || '').toLowerCase() || STREAM_TYPES.RTSP, {
                    key: stream?.id ? `existing-${stream.id}` : randomKey(),
                    title: coerceString(stream?.title),
                    sourceIdentifier: coerceString(stream?.source_identifier),
                    url: coerceString(stream?.url),
                    meta: stream?.meta && typeof stream.meta === 'object' ? stream.meta : {},
                }),
            }));
    }, [buildStreamForType, streams]);

    const resolvedMaxCells = useMemo(() => {
        const numeric = Number(maxCells);
        const minimum = Math.max(normalisedInitial.length, computedCells);
        if (Number.isFinite(numeric) && numeric > 0) {
            return Math.max(numeric, minimum);
        }
        return minimum;
    }, [computedCells, maxCells, normalisedInitial.length]);

    const normalizedGridCellCount = Math.max(Math.ceil(resolvedMaxCells / cols) * cols, cols * rows);
    const displayRows = Math.max(rows, Math.ceil(normalizedGridCellCount / cols));

    const initialCells = useMemo(() => {
        const base = Array.from({ length: normalizedGridCellCount }, () => null);
        let pointer = 0;

        normalisedInitial.forEach((stream) => {
            let desired = typeof stream.position === 'number' ? stream.position : pointer;
            if (desired < 0 || desired >= base.length || base[desired] !== null) {
                while (pointer < base.length && base[pointer] !== null) {
                    pointer += 1;
                }
                desired = pointer < base.length ? pointer : -1;
            }

            if (desired >= 0 && desired < base.length) {
                base[desired] = stream;
                pointer = desired + 1;
            }
        });

        return base;
    }, [normalisedInitial, normalizedGridCellCount]);

    const [cells, setCells] = useState(initialCells);
    const [dropTarget, setDropTarget] = useState(null);
    const draggingIndexRef = useRef(null);
    const pendingOpenKeyRef = useRef(null);
    const [activeEditorKey, setActiveEditorKey] = useState(null);
    const [libraryReplacementDialog, setLibraryReplacementDialog] = useState({ open: false, stream: null });

    useEffect(() => {
        setCells(initialCells);
    }, [initialCells]);

    useEffect(() => {
        if (!activeEditorKey) {
            return;
        }
        const exists = cells.some((cell) => cell && cell.key === activeEditorKey);
        if (!exists) {
            setActiveEditorKey(null);
        }
    }, [activeEditorKey, cells]);

    const buildPayload = useCallback((data) => {
        return data.map((entry, index) => {
            if (!entry) {
                return null;
            }

            let sourceIdentifier = entry.sourceIdentifier ? coerceString(entry.sourceIdentifier) : null;
            let meta = entry.meta && typeof entry.meta === 'object' ? entry.meta : {};
            let url = entry.url || '';

            if (entry.sourceType === STREAM_TYPES.UPLOADED) {
                const file = mediaMap.get(sourceIdentifier ?? '');
                if (file) {
                    sourceIdentifier = coerceString(file.id);
                    url = file.path || '';
                    meta = {
                        file_id: file.id,
                        file_path: file.path,
                        file_name: file.name,
                        upload_name: file.upload_name,
                        type: file.type,
                    };
                }
            }

            if (entry.sourceType === STREAM_TYPES.FLUX) {
                const flux = fluxMap.get(sourceIdentifier ?? '');
                const transportUrl = flux?.link || '';
                if (flux) {
                    sourceIdentifier = coerceString(flux.id);
                    url = transportUrl || '';
                    meta = {
                        flux_id: flux.id,
                        link: flux.link,
                        uploaded_link: flux.uploaded_link,
                        device_id: flux.device_id,
                        transport_url: transportUrl,
                    };
                }
            }

            if (entry.sourceType === STREAM_TYPES.FILE) {
                sourceIdentifier = null;
                if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
                    meta = {};
                }
            }

            if (!meta || typeof meta !== 'object') {
                meta = {};
            }

            return {
                position: index,
                title: entry.title ? entry.title.trim() : '',
                source_type: entry.sourceType,
                source_identifier: sourceIdentifier,
                url,
                meta,
            };
        }).filter(Boolean);
    }, [fluxMap, mediaMap]);

    useEffect(() => {
        syncHiddenField(fieldId, buildPayload(cells));
    }, [cells, buildPayload, fieldId]);

    useEffect(() => {
        if (!formId) {
            return undefined;
        }
        const formElement = document.getElementById(formId);
        if (!formElement) {
            return undefined;
        }

        const handler = () => {
            syncHiddenField(fieldId, buildPayload(cells));
        };

        formElement.addEventListener('formdata', handler);
        return () => formElement.removeEventListener('formdata', handler);
    }, [buildPayload, cells, fieldId, formId]);

    const openEditorForKey = useCallback((key) => {
        if (!key) {
            return;
        }
        setActiveEditorKey(key);
    }, []);

    const closeEditor = useCallback(() => {
        setActiveEditorKey(null);
    }, []);

    useEffect(() => {
        if (!pendingOpenKeyRef.current) {
            return;
        }
        const targetKey = pendingOpenKeyRef.current;
        const exists = cells.some((cell) => cell && cell.key === targetKey);
        if (exists) {
            pendingOpenKeyRef.current = null;
            openEditorForKey(targetKey);
        }
    }, [cells, openEditorForKey]);

    const addStreamAt = useCallback((index) => {
        setCells((current) => {
            if (index < 0 || index >= current.length || current[index]) {
                return current;
            }
            const next = [...current];
            const createdCell = buildStreamForType(determineDefaultType());
            pendingOpenKeyRef.current = createdCell.key;
            next[index] = createdCell;
            return next;
        });
    }, [buildStreamForType, determineDefaultType]);

    const addStream = useCallback(() => {
        setCells((current) => {
            const emptyIndex = current.findIndex((cell) => cell === null);
            if (emptyIndex === -1) {
                return current;
            }
            const next = [...current];
            const createdCell = buildStreamForType(determineDefaultType());
            pendingOpenKeyRef.current = createdCell.key;
            next[emptyIndex] = createdCell;
            return next;
        });
    }, [buildStreamForType, determineDefaultType]);

    const updateStream = useCallback((index, patch) => {
        setCells((current) => current.map((cell, idx) => {
            if (idx !== index || !cell) {
                return cell;
            }

            let next = { ...cell, ...patch };

            if (patch?.sourceType && patch.sourceType !== cell.sourceType) {
                next = buildStreamForType(patch.sourceType, next);
            }

            if (typeof patch?.sourceIdentifier !== 'undefined') {
                if (next.sourceType === STREAM_TYPES.UPLOADED || next.sourceType === STREAM_TYPES.FLUX) {
                    next = buildStreamForType(next.sourceType, { ...next, sourceIdentifier: patch.sourceIdentifier });
                } else {
                    next.sourceIdentifier = coerceString(patch.sourceIdentifier);
                }
            }

            return next;
        }));
    }, [buildStreamForType]);

    const removeStream = useCallback((index) => {
        setCells((current) => {
            if (index < 0 || index >= current.length) {
                return current;
            }
            const target = current[index];
            if (!target) {
                return current;
            }
            const next = [...current];
            next[index] = null;
            if (activeEditorKey === target.key) {
                setActiveEditorKey(null);
            }
            return next;
        });
    }, [activeEditorKey]);

    const swapStreams = useCallback((fromIndex, toIndex) => {
        setCells((current) => {
            if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= current.length || toIndex >= current.length) {
                return current;
            }
            const next = [...current];
            const temp = next[fromIndex];
            next[fromIndex] = next[toIndex];
            next[toIndex] = temp;
            return next;
        });
    }, []);

    const applyLibraryStreamToIndex = useCallback((libraryStream, index, { openEditor = true } = {}) => {
        if (!libraryStream) {
            return;
        }
        setCells((current) => {
            if (index < 0 || index >= current.length) {
                return current;
            }

            const next = [...current];
            const existing = next[index];
            const key = existing?.key || randomKey();

            const mergedMeta = {
                ...(existing?.meta && typeof existing.meta === 'object' ? existing.meta : {}),
                ...(libraryStream.meta && typeof libraryStream.meta === 'object' ? libraryStream.meta : {}),
            };

            if (libraryStream.id) {
                mergedMeta.library_stream_id = libraryStream.id;
            }

            const prepared = buildStreamForType(STREAM_TYPES.RTSP, {
                key,
                title: libraryStream.name || existing?.title || '',
                url: libraryStream.url || existing?.url || '',
                meta: mergedMeta,
                sourceIdentifier: existing?.sourceIdentifier || (libraryStream.id ? `library:${libraryStream.id}` : ''),
            });

            next[index] = prepared;

            if (openEditor) {
                pendingOpenKeyRef.current = key;
            }

            return next;
        });
    }, [buildStreamForType]);

    const replacementStream = libraryReplacementDialog.stream;

    const handleLibraryAdd = useCallback((libraryStream) => {
        if (!libraryStream) {
            return;
        }

        const emptyIndex = cells.findIndex((cell) => cell === null);
        if (emptyIndex !== -1) {
            applyLibraryStreamToIndex(libraryStream, emptyIndex);
            return;
        }

        setLibraryReplacementDialog({ open: true, stream: libraryStream });
    }, [applyLibraryStreamToIndex, cells]);

    const closeReplacementDialog = useCallback(() => {
        setLibraryReplacementDialog({ open: false, stream: null });
    }, []);

    const confirmReplacementAt = useCallback((index) => {
        if (!replacementStream) {
            return;
        }

        applyLibraryStreamToIndex(replacementStream, index);
        setLibraryReplacementDialog({ open: false, stream: null });
    }, [applyLibraryStreamToIndex, replacementStream]);

    const handleDragStart = useCallback((index) => () => {
        draggingIndexRef.current = index;
        setDropTarget(index);
    }, []);

    const handleDragEnd = useCallback(() => {
        draggingIndexRef.current = null;
        setDropTarget(null);
    }, []);

    const handleDragOver = useCallback((index) => (event) => {
        event.preventDefault();
        if (dropTarget !== index) {
            setDropTarget(index);
        }
    }, [dropTarget]);

    const handleDrop = useCallback((index) => (event) => {
        event.preventDefault();
        const fromIndex = draggingIndexRef.current;
        if (fromIndex === null) {
            return;
        }
        swapStreams(fromIndex, index);
        draggingIndexRef.current = null;
        setDropTarget(null);
    }, [swapStreams]);

    const allFluxList = useMemo(() => Array.from(fluxMap.values()), [fluxMap]);
    const filteredFluxList = useMemo(() => allFluxList.filter((flux) => isFluxEligible(flux)), [allFluxList, isFluxEligible]);
    const hasUploadedCells = useMemo(() => cells.some((cell) => cell?.sourceType === STREAM_TYPES.UPLOADED), [cells]);

    const typeDescriptors = useMemo(() => ({
        [STREAM_TYPES.RTSP]: { icon: 'wifi_tethering', label: t('rtspTypeLabel', 'RTSP / HLS') },
        [STREAM_TYPES.UPLOADED]: { icon: 'movie', label: t('uploadedTypeLabel', 'Uploaded media') },
        [STREAM_TYPES.FLUX]: { icon: 'sensors', label: t('fluxTypeLabel', 'Video flux') },
        [STREAM_TYPES.FILE]: { icon: 'folder', label: t('fileTypeLabel', 'Local file') },
        default: { icon: 'video_library', label: t('defaultTypeLabel', 'Stream') },
    }), [t]);

    const typeOptions = useMemo(() => {
        const options = [
            { value: STREAM_TYPES.RTSP, label: t('rtsp', 'RTSP / HLS URL'), disabled: false },
        ];

        options.push({ value: STREAM_TYPES.FILE, label: t('file', 'Local file path'), disabled: false });

        if (resolvedFeatureFlags.allowUploadedMedia || hasUploadedCells) {
            options.push({
                value: STREAM_TYPES.UPLOADED,
                label: t('uploaded', 'Uploaded media'),
                disabled: mediaMap.size === 0 || !resolvedFeatureFlags.allowUploadedMedia,
            });
        }

        options.push({
            value: STREAM_TYPES.FLUX,
            label: t('flux', 'Video flux'),
            disabled: filteredFluxList.length === 0,
        });

        return options;
    }, [filteredFluxList.length, hasUploadedCells, mediaMap.size, resolvedFeatureFlags.allowUploadedMedia, t]);

    const mediaList = useMemo(() => Array.from(mediaMap.values()), [mediaMap]);
    const fluxList = useMemo(() => allFluxList, [allFluxList]);

    const resolveThumbnailForCell = useCallback((cell) => {
        if (!cell) {
            return null;
        }
        const meta = cell.meta && typeof cell.meta === 'object' ? cell.meta : {};
        for (const key of THUMBNAIL_CANDIDATE_KEYS) {
            const candidate = meta[key];
            if (candidate) {
                const resolved = coerceString(candidate);
                if (!failedThumbnailCache.has(resolved) && !isLikelyInvalidThumbnail(resolved)) {
                    return resolved;
                }
            }
        }
        if (cell.sourceType === STREAM_TYPES.FLUX) {
            const flux = fluxMap.get(cell.sourceIdentifier);
            if (flux && typeof flux === 'object') {
                for (const key of THUMBNAIL_CANDIDATE_KEYS) {
                    const candidate = flux[key];
                    if (candidate) {
                        const resolved = coerceString(candidate);
                        if (!failedThumbnailCache.has(resolved) && !isLikelyInvalidThumbnail(resolved)) {
                            return resolved;
                        }
                    }
                }
            }
        }
        if (cell.sourceType === STREAM_TYPES.UPLOADED) {
            const file = mediaMap.get(cell.sourceIdentifier);
            if (file && typeof file === 'object') {
                for (const key of MEDIA_THUMBNAIL_KEYS) {
                    const candidate = file[key];
                    if (candidate) {
                        const resolved = coerceString(candidate);
                        if (!failedThumbnailCache.has(resolved) && !isLikelyInvalidThumbnail(resolved)) {
                            return resolved;
                        }
                    }
                }
            }
        }
        return null;
    }, [fluxMap, mediaMap]);

    const getCellSourceDetail = useCallback((cell) => {
        if (!cell) {
            return '';
        }
        if (cell.sourceType === STREAM_TYPES.UPLOADED) {
            const file = mediaMap.get(cell.sourceIdentifier);
            if (file) {
                return file.name || file.upload_name || `#${file.id}`;
            }
            return t('summaryUploadedFallback', 'Select a media file');
        }
        if (cell.sourceType === STREAM_TYPES.FLUX) {
            const flux = fluxMap.get(cell.sourceIdentifier);
            if (flux) {
                const label = describeFlux(flux);
                if (label) {
                    return label;
                }
            }
            return t('summaryFluxFallback', 'Flux stream');
        }
        if (cell.sourceType === STREAM_TYPES.FILE) {
            return t('summaryFileFallback', 'Local file path');
        }
        return t('summaryRtspFallback', 'Direct stream URL');
    }, [describeFlux, fluxMap, mediaMap, t]);

    const getSummaryUrlDisplay = useCallback((cell) => {
        if (!cell) {
            return '';
        }
        if (cell.sourceType === STREAM_TYPES.UPLOADED) {
            const file = mediaMap.get(cell.sourceIdentifier);
            if (file && file.path) {
                return coerceString(file.path);
            }
        }
        if (cell.sourceType === STREAM_TYPES.FLUX) {
            if (cell.url) {
                return coerceString(cell.url);
            }
            const flux = fluxMap.get(cell.sourceIdentifier);
            if (flux) {
                   return coerceString( flux.link || '');
            }
        }
        return coerceString(cell.url || '');
    }, [fluxMap, mediaMap]);

    const activeEditorIndex = useMemo(() => {
        if (!activeEditorKey) {
            return -1;
        }
        return cells.findIndex((cell) => cell && cell.key === activeEditorKey);
    }, [activeEditorKey, cells]);

    const activeEditorStream = activeEditorIndex >= 0 ? cells[activeEditorIndex] : null;

    const handleDrawerChange = useCallback((patch) => {
        if (activeEditorIndex === -1) {
            return;
        }
        updateStream(activeEditorIndex, patch);
    }, [activeEditorIndex, updateStream]);

    const handleDrawerRemove = useCallback(() => {
        if (activeEditorIndex === -1) {
            return;
        }
        removeStream(activeEditorIndex);
    }, [activeEditorIndex, removeStream]);

    const occupiedCells = cells.filter(Boolean).length;
    const gridViewportStyle = useMemo(() => {
        const needsScroll = displayRows > 3;
        return {
            maxHeight: needsScroll ? 'calc(110vh - 320px)' : 'none',
            overflowY: needsScroll ? 'auto' : 'visible',
            overflowX: 'auto',
            paddingRight: needsScroll ? '0.5rem' : 0,
            marginRight: needsScroll ? '-0.5rem' : 0,
        };
    }, [displayRows]);

    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(300px, 1fr))`,
        gap: '0.5rem',
    };

    const wallTitle = wall?.name ? coerceString(wall.name).trim() : '';
    const headerTitle = wallTitle ? tWith('streamsHeadingWithName', ':name streams', { ':name': wallTitle }) : t('streamsHeading', 'Stream grid');
    const gridSummaryLabel = tWith('gridSummary', 'Grid: :cols x :rows', { ':cols': cols, ':rows': rows });
    const slotsSummaryLabel = tWith('slotsSummary', ':used / :total slots used', { ':used': occupiedCells, ':total': cells.length });

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: '0.75rem' }}>
                <div>
                    <h5 className="mb-1 font-weight-bold">{headerTitle}</h5>
                    <small className="text-muted">{gridSummaryLabel}</small>
                </div>
                <span className="badge badge-rose badge-pill" style={{ fontSize: '0.85rem' }}>
                    {slotsSummaryLabel}
                </span>
            </div>

            <div style={gridViewportStyle}>
                <div style={gridStyle}>
                    {Array.from({ length: cols * displayRows }).map((_, index) => {
                    const cell = index < cells.length ? cells[index] : null;
                    const isActive = dropTarget === index;
                    const rowNumber = Math.floor(index / cols) + 1;
                    const colNumber = (index % cols) + 1;
                    const positionLabel = tWith('positionLabel', 'Position :rowx:col', { ':row': rowNumber, ':col': colNumber });
                    const mergedStyle = isActive ? { ...baseCellStyle, ...activeCellStyle } : baseCellStyle;

                    const descriptor = cell ? (typeDescriptors[cell.sourceType] || typeDescriptors.default) : typeDescriptors.default;
                    const summaryTitle = cell ? (cell.title && cell.title.trim() ? cell.title.trim() : t('untitledStream', 'Untitled stream')) : '';
                    const sourceDetail = cell ? getCellSourceDetail(cell) : '';
                    const summaryUrlFull = cell ? getSummaryUrlDisplay(cell) : '';
                    const summaryUrlDisplay = (() => {
                        if (!summaryUrlFull) {
                            return '';
                        }
                        if (cell?.sourceType === STREAM_TYPES.FILE) {
                            return summaryUrlFull;
                        }
                        if (cell?.sourceType === STREAM_TYPES.UPLOADED) {
                            return extractFileName(summaryUrlFull) || summaryUrlFull;
                        }
                        if (/^[a-z]+:\/\//i.test(summaryUrlFull)) {
                            return summaryUrlFull;
                        }
                        return extractFileName(summaryUrlFull) || summaryUrlFull;
                    })();
                    const thumbnailUrl = cell ? resolveThumbnailForCell(cell) : null;

                        return (
                            <div
                                key={`cell-${index}`}
                                style={mergedStyle}
                                onDragOver={handleDragOver(index)}
                                onDrop={handleDrop(index)}
                                onDragLeave={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                        setDropTarget(null);
                                    }
                                }}
                            >
                                {cell ? (
                                    <StreamSummaryCard
                                        positionLabel={positionLabel}
                                        title={summaryTitle}
                                        typeLabel={descriptor.label}
                                        typeIcon={descriptor.icon}
                                        sourceDetail={sourceDetail}
                                        urlDisplay={summaryUrlDisplay || t('summaryNoUrl', 'No stream URL yet')}
                                        urlTooltip={summaryUrlFull || t('summaryNoUrl', 'No stream URL yet')}
                                        thumbnailUrl={thumbnailUrl || undefined}
                                        onEdit={() => openEditorForKey(cell.key)}
                                        onRemove={() => removeStream(index)}
                                        onCardClick={() => openEditorForKey(cell.key)}
                                        removeLabel={t('remove', 'Remove')}
                                        editLabel={t('edit', 'Edit')}
                                        draggable
                                        onDragStart={handleDragStart(index)}
                                        onDragEnd={handleDragEnd}
                                        onThumbnailError={(failedUrl) => {
                                            if (failedUrl) {
                                                failedThumbnailCache.add(failedUrl);
                                            }
                                        }}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-outline-rose"
                                        style={emptyCellButtonStyle}
                                        onClick={() => addStreamAt(index)}
                                    >
                                        <i className="material-icons" style={{ fontSize: '32px' }}>add</i><br />
                                        <span>{t('addStreamHere', 'Add stream here')}</span>
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-4">
                <button type="button" className="btn btn-rose" onClick={addStream} disabled={cells.every((cell) => cell !== null)}>
                    <i className="material-icons">add</i>
                    <span className="ml-2">{t('addStream', 'Add stream')}</span>
                </button>
                <small className="text-muted">{t('dragHint', 'Tip: drag tiles to rearrange their positions.')}</small>
            </div>

            {library && (
                <div className="mt-5">
                    <h5 className="mb-3 font-weight-bold">{t('librarySectionHeading', 'Saved streams')}</h5>
                    <StreamLibraryPicker
                        initialStreams={Array.isArray(library.initialStreams) ? library.initialStreams : []}
                        initialMeta={library.initialMeta || {}}
                        endpoints={library.endpoints || {}}
                        csrfToken={library.csrfToken || ''}
                        manageUrl={library.manageUrl || ''}
                        onAdd={handleLibraryAdd}
                    />
                </div>
            )}

            {library && (
                <Dialog
                    open={libraryReplacementDialog.open}
                    onClose={closeReplacementDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>{t('libraryReplaceTitle', 'Choose a tile to replace')}</DialogTitle>
                    <DialogContent dividers>
                        <MuiTypography variant="body2" color="text.secondary" paragraph>
                            {t('libraryReplaceDescription', 'Your wall is full. Select a tile to replace with the saved stream.')}
                        </MuiTypography>
                        {cells.every((cell) => cell === null) ? (
                            <MuiTypography variant="body2" color="text.secondary">
                                {t('libraryNoTiles', 'No stream tiles are currently configured.')}
                            </MuiTypography>
                        ) : (
                            <MuiList disablePadding>
                                {cells.map((cell, index) => {
                                    if (!cell) {
                                        return null;
                                    }
                                    const rowNumber = Math.floor(index / cols) + 1;
                                    const colNumber = (index % cols) + 1;
                                    const positionLabel = tWith('positionLabel', 'Position :rowx:col', { ':row': rowNumber, ':col': colNumber });
                                    const summaryTitle = cell.title && cell.title.trim() ? cell.title.trim() : t('untitledStream', 'Untitled stream');
                                    const descriptor = typeDescriptors[cell.sourceType] || typeDescriptors.default;
                                    const sourceDetail = getCellSourceDetail(cell);
                                    const summaryUrl = getSummaryUrlDisplay(cell);

                                    return (
                                        <ListItemButton key={`replace-${index}`} dense onClick={() => confirmReplacementAt(index)}>
                                            <ListItemText
                                                primary={`${positionLabel} · ${summaryTitle}`}
                                                secondary={(
                                                    <span>
                                                        <MuiTypography component="span" variant="caption" color="text.secondary">
                                                            {descriptor.label}
                                                        </MuiTypography>
                                                        {sourceDetail ? (
                                                            <MuiTypography
                                                                component="span"
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block' }}
                                                            >
                                                                {sourceDetail}
                                                            </MuiTypography>
                                                        ) : null}
                                                        {summaryUrl ? (
                                                            <MuiTypography
                                                                component="span"
                                                                variant="caption"
                                                                color="text.disabled"
                                                                sx={{ display: 'block', wordBreak: 'break-all' }}
                                                            >
                                                                {summaryUrl}
                                                            </MuiTypography>
                                                        ) : null}
                                                    </span>
                                                )}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </MuiList>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MuiButton onClick={closeReplacementDialog} color="inherit">
                            {t('libraryCancel', 'Cancel')}
                        </MuiButton>
                    </DialogActions>
                </Dialog>
            )}

            <StreamEditDrawer
                open={Boolean(activeEditorStream)}
                onClose={closeEditor}
                stream={activeEditorStream}
                typeOptions={typeOptions}
                translations={translations}
                mediaFiles={mediaList}
                fluxes={fluxList}
                featureFlags={resolvedFeatureFlags}
                onChange={handleDrawerChange}
                onRemove={handleDrawerRemove}
            />
        </div>
    );
};

export default DisplayWallStreamsEditor;
